import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axiosInstance from '../api/axiosInstance';

function ForumOverview() {
  const [forums, setForums] = useState([]);
  const [lastPostPages, setLastPostPages] = useState({});
  const [recentThreads, setRecentThreads] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(false);
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // Helper function to calculate page number for a post
  const calculatePostPage = async (threadId, postId) => {
    try {
      const response = await axiosInstance.get(`/posts/thread/${threadId}/post/${postId}`);
      const postsPerPage = 20; // Should match backend limit
      const postIndex = response.data.index;
      const page = Math.ceil((postIndex + 1) / postsPerPage);
      return page;
    } catch (error) {
      console.error('Error calculating post page:', error);
      
      // Try alternative approach - get thread info to calculate pages
      try {
        const threadResponse = await axiosInstance.get(`/posts/${threadId}?limit=1`);
        const totalPosts = threadResponse.data.pagination?.total || 0;
        const totalPages = Math.ceil(totalPosts / 20);
        return totalPages || 1;
      } catch (altError) {
        console.error('Alternative approach also failed:', altError);
        return 1; // Fallback to page 1
      }
    }
  };

  useEffect(() => {
    // Foren laden
    axiosInstance.get('/forums')
      .then(async (res) => {
        setForums(res.data);
        
        // Seiten für letzte Posts berechnen - optimiert mit Promise.all
        const pagesInfo = {};
        const pageCalculations = [];
        
        // Collect all page calculations
        for (const forum of res.data) {
          if (forum.subforums) {
            for (const sub of forum.subforums) {
              if (sub.latestPost && sub.latestPost.postId && sub.latestPost.threadId) {
                pageCalculations.push({
                  subforumId: sub._id,
                  threadId: sub.latestPost.threadId,
                  postId: sub.latestPost.postId,
                  promise: calculatePostPage(sub.latestPost.threadId, sub.latestPost.postId)
                });
              } else {
                console.log(`No valid latest post found for ${sub.title}`);
              }
            }
          }
        }
        
        
        // Execute all calculations in parallel
        const results = await Promise.allSettled(
          pageCalculations.map(calc => calc.promise)
        );
        
        // Process results
        pageCalculations.forEach((calc, index) => {
          const result = results[index];
          const page = result.status === 'fulfilled' ? result.value : 1;
          
          pagesInfo[calc.subforumId] = {
            threadId: calc.threadId,
            postId: calc.postId,
            page: page,
          };
        });
        
        setLastPostPages(pagesInfo);
      })
      .catch(err => console.error('Fehler beim Laden der Foren:', err));
  }, []);

  // Function to load recent threads (5 latest)
  const loadRecentThreads = async () => {
    try {
      setLoadingRecent(true);
      const res = await axiosInstance.get('/thread-activity/recent?page=1&limit=5');
      const threads = res.data.threads || [];
      
      // Sort threads by last post date (newest first)
      const sortedThreads = threads.sort((a, b) => {
        const aDate = a.lastPost?.createdAt || a.createdAt;
        const bDate = b.lastPost?.createdAt || b.createdAt;
        return new Date(bDate) - new Date(aDate);
      });
      
      setRecentThreads(sortedThreads.slice(0, 5));
      setLoadingRecent(false);
    } catch (err) {
      console.error('Error loading recent threads:', err);
      setLoadingRecent(false);
    }
  };

  // Function to load forum statistics
  const loadStats = async () => {
    try {
      setLoadingStats(true);
      const res = await axiosInstance.get('/stats');
      setStats(res.data);
      setLoadingStats(false);
    } catch (err) {
      console.error('Error loading stats:', err);
      // Fallback to basic stats if API not available
      const fallbackStats = {
        activeVisitors: { 
          total: 1, // At least the current user
          loggedIn: localStorage.getItem('token') ? 1 : 0, 
          guests: localStorage.getItem('token') ? 0 : 1, 
          loggedInUsers: localStorage.getItem('token') ? ['Du'] : [] 
        },
        userStats: { 
          total: 0, 
          active: 0, 
          newest: null 
        },
        contentStats: { 
          forums: forums.length || 0, 
          threads: 0, 
          posts: 0 
        }
      };
      setStats(fallbackStats);
      setLoadingStats(false);
    }
  };

  // Function to send heartbeat
  const sendHeartbeat = async () => {
    try {
      // Try both token keys that might be used
      const accessToken = localStorage.getItem('accessToken');
      const token = localStorage.getItem('token');
      const authToken = accessToken || token;
      
      let currentUser = null;
      
      if (authToken) {
        try {
          // Simple decode without library dependency
          const payload = JSON.parse(atob(authToken.split('.')[1]));
          currentUser = payload;
          console.log('Decoded token payload:', payload); // Debug logging
        } catch (e) {
          console.log('Token decode failed:', e);
        }
      } else {
        console.log('No token found in localStorage');
      }

      const heartbeatData = {
        username: currentUser?.username,
        userId: currentUser?.id
      };
      
      console.log('Sending heartbeat:', heartbeatData); // Debug logging
      await axiosInstance.post('/stats/heartbeat', heartbeatData);
      console.log('Heartbeat sent successfully');
    } catch (err) {
      console.log('Heartbeat failed:', err.response?.status, err.response?.data || err.message);
    }
  };

  useEffect(() => {
    // Foren laden
    axiosInstance.get('/forums')
      .then(async (res) => {
        setForums(res.data);
        
        // Seiten für letzte Posts berechnen - optimiert mit Promise.all
        const pagesInfo = {};
        const pageCalculations = [];
        
        // Collect all page calculations
        for (const forum of res.data) {
          if (forum.subforums) {
            for (const sub of forum.subforums) {
              if (sub.latestPost && sub.latestPost.postId && sub.latestPost.threadId) {
                pageCalculations.push({
                  subforumId: sub._id,
                  threadId: sub.latestPost.threadId,
                  postId: sub.latestPost.postId,
                  promise: calculatePostPage(sub.latestPost.threadId, sub.latestPost.postId)
                });
              } else {
                console.log(`No valid latest post found for ${sub.title}`);
              }
            }
          }
        }
        
        
        // Execute all calculations in parallel
        const results = await Promise.allSettled(
          pageCalculations.map(calc => calc.promise)
        );
        
        // Process results
        pageCalculations.forEach((calc, index) => {
          const result = results[index];
          const page = result.status === 'fulfilled' ? result.value : 1;
          
          pagesInfo[calc.subforumId] = {
            threadId: calc.threadId,
            postId: calc.postId,
            page: page,
          };
        });
        
        setLastPostPages(pagesInfo);
      })
      .catch(err => console.error(err));
    
    // Load recent threads
    loadRecentThreads();
    
    // Load stats
    loadStats();
    
    // Send initial heartbeat
    sendHeartbeat();
    
    // Set up heartbeat interval (every 3 minutes)
    const heartbeatInterval = setInterval(sendHeartbeat, 3 * 60 * 1000);
    
    return () => {
      clearInterval(heartbeatInterval);
    };
  }, []);

  const truncate = (str, length) => 
    str.length > length ? str.substring(0, length) + '...' : str;

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleString('de-DE', {
      dateStyle: 'short',
      timeStyle: 'short',
    });

  const getLastPostUrl = (sub) => {
    const info = lastPostPages[sub._id];
    
    if (!info) {
      const postId = sub.latestPost?.postId;
      if (!postId) {
        return sub.latestPost?.threadId ? `/thread/${sub.latestPost.threadId}` : '#';
      }
      return `/thread/${sub.latestPost.threadId}#post-${postId}`;
    }
    
    if (!info.postId || !info.threadId) {
      return '#';
    }
    
    return `/thread/${info.threadId}?page=${info.page}#post-${info.postId}`;
  };

  const getRecentThreadUrl = (thread) => {
    // If there's a last post, calculate the page and link to it
    if (thread.lastPost && thread.lastPost._id) {
      const postsPerPage = 20;
      const postCount = thread.postCount || 1;
      const lastPage = Math.ceil(postCount / postsPerPage);
      
      if (lastPage > 1) {
        return `/thread/${thread._id}?page=${lastPage}#post-${thread.lastPost._id}`;
      } else {
        return `/thread/${thread._id}#post-${thread.lastPost._id}`;
      }
    }
    
    // Fallback to just the thread
    return `/thread/${thread._id}`;
  };

  return (
    <div className="w-full px-2 sm:px-4">
      <div className="text-center mb-12">
        <h1 className="text-5xl sm:text-6xl text-white font-marker mb-4 animate-fade-in">
          Foren-Übersicht
        </h1>
        
      </div>

      <div className="space-y-8">
        {forums.map((forum, index) => (
          <div
            key={forum._id}
            className="modern-card group hover:scale-[1.02] transition-all duration-300"
            style={{ animationDelay: `${index * 100}ms` }}
          >
            <div className="px-6 py-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                  <div className="w-3 h-3 bg-primary rounded-full"></div>
                  {forum.title}
                </h2>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {forum.subforums?.length || 0} Unterforen
                </div>
              </div>

              {forum.subforums && forum.subforums.length > 0 && (
                <div className="grid gap-4">
                  {forum.subforums.map(sub => (
                    <div
                      key={sub._id}
                      className="bg-gray-50/50 dark:bg-gray-800/50 rounded-2xl p-5 border border-gray-200/50 dark:border-gray-700/50 hover:border-primary/30 transition-all duration-200 group/subforum"
                    >
                      <div className="flex flex-col lg:flex-row lg:justify-between gap-4">
                        <div className="flex-1">
                          <Link
                            to={`/forum/${sub._id}`}
                            className="font-semibold text-lg hover:text-primary transition-colors duration-200 text-gray-800 dark:text-white group-hover/subforum:text-primary"
                          >
                            {sub.title}
                          </Link>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                            {sub.description}
                          </p>
                          <div className="flex items-center gap-4 mt-3 text-xs text-gray-500 dark:text-gray-400">
                            <span className="flex items-center gap-1">
                              <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                              {sub.threadCount ?? 0} Threads
                            </span>
                            <span className="flex items-center gap-1">
                              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                              {sub.postCount ?? 0} Beiträge
                            </span>
                          </div>
                          {(sub.threadCount ?? 0) > 200 && (
                            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-red-500 to-orange-500 text-white text-xs font-semibold px-3 py-1 rounded-full mt-2">
                              🔥 Heiß diskutiert
                            </div>
                          )}
                        </div>
                        <div className="flex-shrink-0 text-right min-w-[200px]">
                          <div className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-2">
                            Letzter Beitrag:
                          </div>
                          {sub.latestPost ? (
                            <Link
                              to={getLastPostUrl(sub)}
                              className="hover:text-primary transition-colors duration-200 block"
                            >
                              <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {truncate(sub.latestPost.title, 35)}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                von {sub.latestPost.author}
                              </div>
                              <div className="text-xs text-gray-400">
                                {formatDate(sub.latestPost.createdAt)}
                              </div>
                            </Link>
                          ) : (
                            <div className="text-sm text-gray-500 dark:text-gray-400 italic">
                              Noch keine Beiträge
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Recent Threads Section */}
      <div className="mt-16 mb-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl sm:text-4xl text-white font-marker mb-2">
            Neueste Beiträge
          </h2>
          
        </div>

        {loadingRecent ? (
          <div className="flex justify-center">
            <div className="modern-card p-8">
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <span className="ml-3 text-gray-600 dark:text-gray-300">Lade neueste Beiträge...</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {recentThreads.map((thread, index) => (
              <div
                key={thread._id}
                className="modern-card group hover:scale-[1.02] transition-all duration-300"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="p-4">
                  <Link
                    to={getRecentThreadUrl(thread)}
                    className="block"
                  >
                    <h3 className="font-semibold text-sm hover:text-primary transition-colors duration-200 text-gray-800 dark:text-white group-hover:text-primary mb-2 line-clamp-2">
                      {truncate(thread.title, 50)}
                    </h3>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                      von {thread.author?.username || 'Unbekannt'}
                    </div>
                    {thread.lastPost && (
                      <div className="text-xs text-gray-400 border-t pt-2">
                        <div className="mb-1">
                          Letzter Beitrag von {thread.lastPost.author?.username || '—'}
                        </div>
                        <div>
                          {formatDate(thread.lastPost.createdAt)}
                        </div>
                      </div>
                    )}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Forum Statistics Section */}
        <div className="mt-12">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl text-white font-marker mb-2">
              Forum Statistiken
            </h2>
            <p className="text-lg text-gray-300">
              Aktuelle Aktivität und Zahlen
            </p>
          </div>

          {loadingStats ? (
            <div className="flex justify-center">
              <div className="modern-card p-8">
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  <span className="ml-3 text-gray-600 dark:text-gray-300">Lade Statistiken...</span>
                </div>
              </div>
            </div>
          ) : stats ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Active Visitors */}
              <div className="modern-card p-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-white text-xl">👥</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">
                    Aktive Besucher
                  </h3>
                  <div className="text-3xl font-bold text-primary mb-2">
                    {stats.visitors?.total || 0}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400 space-y-1">
                    <div>{stats.visitors?.users || 0} eingeloggt</div>
                    <div>{stats.visitors?.guests || 0} Gäste</div>
                  </div>
                </div>
              </div>

              {/* Total Users */}
              <div className="modern-card p-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-white text-xl">👤</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">
                    Mitglieder
                  </h3>
                  <div className="text-3xl font-bold text-primary mb-2">
                    {stats.users?.total || 0}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400 space-y-1">
                    <div>{stats.users?.active || 0} aktive</div>
                    {stats.users?.newest?.username && (
                      <div>Neuester: {stats.users.newest.username}</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Forum Content */}
              <div className="modern-card p-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-white text-xl">💬</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">
                    Diskussionen
                  </h3>
                  <div className="text-3xl font-bold text-primary mb-2">
                    {stats.content?.threads || 0}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400 space-y-1">
                    <div>{stats.content?.forums || 0} Foren</div>
                    <div>Threads</div>
                  </div>
                </div>
              </div>

              {/* Total Posts */}
              <div className="modern-card p-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-white text-xl">📝</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">
                    Beiträge
                  </h3>
                  <div className="text-3xl font-bold text-primary mb-2">
                    {stats.content?.posts || 0}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Gesamt
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {/* Online Users List */}
          {stats?.activeVisitors?.loggedInUsers && stats.activeVisitors.loggedInUsers.length > 0 && (
            <div className="modern-card mt-6 p-6">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 text-center">
                Online Mitglieder
              </h3>
              <div className="flex flex-wrap justify-center gap-2">
                {stats.activeVisitors.loggedInUsers.map((user, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary/10 text-primary border border-primary/20"
                  >
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                    {user}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ForumOverview;
